import { matchPath } from "react-router-dom";

/**
 * Páginas públicas DAS IGREJAS: a bio, os formulários, eventos, chamadas,
 * escalas. Quem abre é membro ou visitante da igreja, não cliente do
 * Ecclesiato.
 *
 * Nelas não entram o aviso de cookies nem os rastreadores de marketing do
 * Ecclesiato (GTM, GA4, Google Ads, Meta Pixel — analytics-loader.tsx e
 * use-page-tracking.ts). Com eles, cada membro que aceitava os cookies na bio
 * da igreja virava PageView no Pixel do Ecclesiato: o público de anúncio e os
 * semelhantes passavam a se basear em quem frequenta igreja, e não em quem
 * compra o sistema. E a página da igreja abria perguntando de cookies com a
 * política de privacidade do Ecclesiato.
 *
 * O que continua: o DataFast (metas de produto) e o analytics que a própria
 * igreja vê, gravado na conta dela — nenhum dos dois depende de cookie.
 */

/**
 * Primeiro segmento de todas as rotas do sistema. A bio mora na raiz
 * (/:slug), então isto serve para duas coisas: impedir que uma igreja escolha
 * um endereço que já é página do sistema (a rota do sistema venceria e a bio
 * ficaria inalcançável) e reconhecer a bio pelo que sobra.
 *
 * Rota nova no primeiro nível entra aqui — senão vira "bio" para o aviso de
 * cookies e fica livre como endereço de igreja.
 */
export const RESERVED_SLUGS = [
  "__erro",
  "about",
  "admin",
  "agenda",
  "api",
  "apelo",
  "assets",
  "atividades",
  "avaliar",
  "batismos",
  "bio",
  "bio-links",
  "blog",
  "cantina",
  "carteirinha",
  "centraldomembro",
  "chamada",
  "comparar",
  "comunidade",
  "configuracoes",
  "conta",
  "cronograma",
  "css",
  "cultos",
  "dashboard",
  "diagnostico",
  "escalas",
  "estatisticas",
  "evangelismo",
  "events",
  "faq",
  "favicon",
  "features",
  "feedback",
  "ferramentas",
  "financas",
  "fonts",
  "frequentadores",
  "grupos",
  "igreja",
  "images",
  "img",
  "js",
  "kids",
  "login",
  "loja",
  "manifest.json",
  "membros",
  "ministerios",
  "ministerios-dash",
  "obrigado",
  "onboarding",
  "oracao",
  "pessoas",
  "politica-privacidade",
  "politica-reembolsos",
  "pricing",
  "projetos",
  "proposta",
  "public",
  "register",
  "relatorios",
  "reset-password",
  "robots.txt",
  "sitemap.xml",
  "static",
  "termos-de-uso",
  "treinamentos",
  "verify-email",
  "visitantes",
];

const RESERVED = new Set(RESERVED_SLUGS);

// Os padrões das páginas públicas das igrejas (ver routes.tsx)
const CHURCH_PUBLIC_PATTERNS = [
  "/apelo/:idDaIgreja",
  "/agenda/:idDaIgreja/*",
  "/visitantes/:idDaIgreja",
  "/pessoas/membros/:idDaIgreja",
  "/pessoas/pedidos-oracao/:idDaIgreja",
  "/chamada/:idDaIgreja/*",
  "/ministerios/chamada/:idDaIgreja/*",
  "/ministerios/jornadas/form/*",
  "/events/:idDaIgreja/*",
  "/escalas/disponibilidade/*",
  "/escalas/preferencias/*",
  "/escalas/presenca/*",
  "/escalas/publico/*",
  "/escalas/v/*",
  "/cronograma/:slug",
  "/centraldomembro/:idDaIgreja",
  "/cultos/contagem/:idDaIgreja",
  "/cultos/presenca/:idDaIgreja",
  "/grupos/frequencia/:churchId",
  "/kids/criancas/:idDaIgreja",
  "/oracao/:idDaIgreja/:chainId",
  "/evangelismo/:idDaIgreja/:campaignId",
  "/treinamentos/publico/*",
  "/public/vote/*",
  // Não é de igreja: é a página que o Chrome do servidor abre para imprimir a
  // carteirinha avulsa. Entra aqui para não ganhar aviso de cookies (que sairia
  // impresso) nem contar como visita no marketing.
  "/carteirinha/impressao",
];

// "/membros/:idDaIgreja" é público, mas divide o prefixo com telas do painel
const MEMBROS_ADMIN = new Set(["cadastro", "arvore", "editar", "visualizar"]);

export function isChurchPublicPath(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean);

  // A bio: um segmento só que não é rota do sistema
  if (segments.length === 1 && !RESERVED.has(segments[0].toLowerCase())) {
    return true;
  }

  const membros = matchPath("/membros/:id", pathname);
  if (membros && !MEMBROS_ADMIN.has(membros.params.id || "")) return true;

  return CHURCH_PUBLIC_PATTERNS.some((pattern) => !!matchPath(pattern, pathname));
}
